'use client';

import { REQUIRE_AUTH_EVENT } from '@ahm/api-wrappers-core';
import { session as sessionStore } from '@ahm/common-helpers';
import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from '../auth-provider';
import { AuthStatus, AuthTab } from '../lib/constants';
import {
  useCustomRouter,
  usePathname,
  useSearchParams,
} from '../lib/navigation';
import { getRedirectUrl } from '../lib/utils';
import { type Locales, authLocales, createTranslations } from '../locales';
import AuthModal from '../modals/auth-modal';
import ExpiredTokenModal from '../modals/expired-token-modal';
import { ROUTES } from '../router';
import { useAuthStore } from '../store/auth-store-provider';

export default function AuthProtection({
  children,
  lang = 'vi',
}: PropsWithChildren<{ lang?: keyof Locales }>) {
  const { status, handleOpenModalAuth, expiredModalState } = useAuthProtection({
    lang,
  });

  if (status !== AuthStatus.Authenticated) {
    return (
      <>
        {children}
        <ExpiredTokenModal
          {...expiredModalState}
          onClose={() => handleOpenModalAuth()}
        />
        <AuthModal opened={expiredModalState.openAuthModal} />
      </>
    );
  }

  return <>{children}</>;
}

function useAuthProtection({ lang = 'vi' }: { lang?: keyof Locales } = {}) {
  const tAuth = createTranslations({ locales: authLocales, lang });
  const pathname = usePathname();
  const { navigate } = useCustomRouter();
  const { logout } = useAuth();
  const searchParams = useSearchParams();
  const tokenSearchParam = searchParams.get('token');

  const {
    status,
    setSession,
    accountsEndpoint,
    logout: logoutStore,
  } = useAuthStore((state) => state);

  const authenExpired = useMemo(
    () =>
      sessionStore.getItem('authenError')
        ? JSON.parse(sessionStore.getItem('authenError')!)
        : null,
    []
  );
  const [expiredModalState, setExpiredModalState] = useState({
    opened: false,
    title: '',
    content: '',
    openAuthModal: false,
    isLogout: false,
  });
  const [shouldOpenAuthModal, setShouldOpenAuthModal] = useState(false);

  const handleOpenModalAuth = useCallback(() => {
    setExpiredModalState((prev) => ({ ...prev, opened: false }));
    setShouldOpenAuthModal(true);
  }, []);

  const handleCloseExpiredModal = useCallback(() => {
    setExpiredModalState((prev) => ({ ...prev, opened: false }));

    if (authenExpired) {
      logout();
      setShouldOpenAuthModal(true);
    } else {
      const { origin, pathname, search } = window.location;

      if (origin === accountsEndpoint) {
        handleOpenModalAuth();
        return;
      }

      const redirectUrl = window.encodeURIComponent(
        `${origin}${pathname}${search}`
      );
      navigate(
        `${accountsEndpoint}${ROUTES.signIn}?redirect_uri=${redirectUrl}`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenExpired, logout, navigate]);

  useEffect(() => {
    if (shouldOpenAuthModal) {
      setExpiredModalState((prev) => ({ ...prev, openAuthModal: true }));
      setShouldOpenAuthModal(false);
    }
  }, [shouldOpenAuthModal]);

  const showModalExpire = useCallback(() => {
    if (status === AuthStatus.Unauthenticated) {
      setExpiredModalState({
        opened: true,
        title: authenExpired
          ? authenExpired.title
          : tAuth('youAreNotLoggedInYet'),
        content: authenExpired
          ? authenExpired.description
          : tAuth('pleaseLogInToContinue'),
        openAuthModal: false,
        isLogout: Boolean(authenExpired),
      });
    }
  }, [authenExpired, status, tAuth]);

  useEffect(() => {
    if (
      pathname !== AuthTab.SignIn.toString() &&
      pathname !== AuthTab.SignUp.toString()
    ) {
      showModalExpire();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, status]);

  useEffect(() => {
    if (status === AuthStatus.Authenticated) {
      const { url, type } = getRedirectUrl(status, pathname, searchParams);
      if (type === 'internal' && url !== pathname) {
        navigate(url);
      } else if (type === 'external') {
        window.location.href = url;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const loginViaToken = useCallback(() => {
    if (
      pathname === AuthTab.SignIn.toString() ||
      pathname === AuthTab.SignUp.toString() ||
      !tokenSearchParam
    ) {
      return;
    }

    setSession({
      token: tokenSearchParam,
    });
  }, [pathname, tokenSearchParam, setSession]);

  useEffect(() => {
    loginViaToken();
  }, [loginViaToken]);

  useEffect(() => {
    const handler = (event: CustomEvent) => {
      if (event?.detail && typeof event.detail.isTokenExpired === 'boolean') {
        logoutStore(event.detail.isTokenExpired);
      }
    };

    window.addEventListener(REQUIRE_AUTH_EVENT, handler as EventListener);

    // Optionally return a function to remove the listener
    return () => {
      window.removeEventListener(REQUIRE_AUTH_EVENT, handler as EventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    status,
    handleCloseExpiredModal,
    expiredModalState,
    handleOpenModalAuth,
  };
}
